import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

export interface SprintOverride {
  workstreamId: string;
  sprintIndex: number;
  velocity: number;
  memberCapacities: Record<string, number>;
}

/** Lifecycle status of a workstream sprint shown in the forecaster. */
export type WorkstreamSprintStatus = 'Closed' | 'Active' | 'Scheduled';

/** An official sprint belonging to a specific workstream. */
export interface WorkstreamSprint {
  /** Stable UUID. */
  id: string;
  /** Foreign key → Workstream.id. */
  workstreamId: string;
  /** Sequential 1-based position within the workstream (1, 2, 3, …). */
  number: number;
  /** Optional display alias shown alongside the sprint number. */
  nickname?: string;
  /** IDs of stories officially assigned to this sprint. */
  storyIds: string[];
  /** Current lifecycle stage of this sprint for visual display in the forecaster. */
  status?: WorkstreamSprintStatus;
}

interface SprintsState {
  startDate: string; // ISO date string, first Monday of timeline
  sprintLength: number; // days (default 10)
  totalSprints: number;
  overrides: SprintOverride[];
  /** Official sprints per workstream — keyed by workstreamId on each record. */
  workstreamSprints: WorkstreamSprint[];
}

export const DEFAULT_SPRINT_LENGTH = 10;
export const DEFAULT_TOTAL_SPRINTS = 8;

const initialState: SprintsState = {
  startDate: (() => {
    // Find next Monday
    const d = new Date();
    const day = d.getDay();
    const diff = day === 1 ? 0 : (8 - day) % 7;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  })(),
  sprintLength: DEFAULT_SPRINT_LENGTH,
  totalSprints: DEFAULT_TOTAL_SPRINTS,
  overrides: [],
  workstreamSprints: [],
};

const sprintsSlice = createSlice({
  name: 'sprints',
  initialState,
  reducers: {
    setStartDate: (state, action: PayloadAction<string>) => {
      state.startDate = action.payload;
    },
    setSprintLength: (state, action: PayloadAction<number>) => {
      state.sprintLength = action.payload;
    },
    setTotalSprints: (state, action: PayloadAction<number>) => {
      state.totalSprints = action.payload;
    },
    setSprintOverride: (state, action: PayloadAction<SprintOverride>) => {
      const idx = state.overrides.findIndex(
        o => o.workstreamId === action.payload.workstreamId && o.sprintIndex === action.payload.sprintIndex
      );
      if (idx !== -1) state.overrides[idx] = action.payload;
      else state.overrides.push(action.payload);
    },
    setSprintSettings: (state, action: PayloadAction<{ startDate: string; sprintLength: number; totalSprints: number }>) => {
      state.startDate = action.payload.startDate;
      state.sprintLength = action.payload.sprintLength;
      state.totalSprints = action.payload.totalSprints;
      // Clear overrides when applying a full sprint settings reset to avoid
      // stale per-sprint overrides conflicting with the new configuration.
      state.overrides = [];
    },
    clearSprintOverrides: (state) => {
      state.overrides = [];
    },
    removeMemberFromOverrides: (state, action: PayloadAction<string>) => {
      state.overrides.forEach((override) => {
        delete override.memberCapacities[action.payload];
      });
    },

    // ── WorkstreamSprint CRUD ─────────────────────────────────────────────

    /** Append a new sprint for a workstream. `number` must equal the current
     *  max sprint number for that workstream + 1 (sequential ordering). */
    addWorkstreamSprint: (state, action: PayloadAction<WorkstreamSprint>) => {
      state.workstreamSprints.push(action.payload);
    },

    /** Update the nickname or storyIds of an existing WorkstreamSprint. */
    updateWorkstreamSprint: (state, action: PayloadAction<WorkstreamSprint>) => {
      const idx = state.workstreamSprints.findIndex((s) => s.id === action.payload.id);
      if (idx !== -1) state.workstreamSprints[idx] = action.payload;
    },

    /** Remove a sprint by id and re-number subsequent sprints for that workstream. */
    removeWorkstreamSprint: (state, action: PayloadAction<{ workstreamId: string; sprintId: string }>) => {
      const { workstreamId, sprintId } = action.payload;
      state.workstreamSprints = state.workstreamSprints.filter(
        (s) => !(s.workstreamId === workstreamId && s.id === sprintId),
      );
      // Re-number remaining sprints for the workstream to keep them sequential.
      const remaining = state.workstreamSprints
        .filter((s) => s.workstreamId === workstreamId)
        .sort((a, b) => a.number - b.number);
      remaining.forEach((s, i) => { s.number = i + 1; });
    },

    /** Replace the ordered list of sprints for a workstream (used by drag-and-drop).
     *  Renumbers all sprints so no gaps can form. */
    reorderWorkstreamSprints: (state, action: PayloadAction<{ workstreamId: string; sprints: WorkstreamSprint[] }>) => {
      const { workstreamId, sprints } = action.payload;
      // Remove old sprints for this workstream.
      state.workstreamSprints = state.workstreamSprints.filter((s) => s.workstreamId !== workstreamId);
      // Re-insert renumbered sprints.
      sprints.forEach((s, i) => {
        state.workstreamSprints.push({ ...s, number: i + 1 });
      });
    },

    /** Bulk-replace all workstream sprints (used for scenario loading/reset). */
    setWorkstreamSprints: (state, action: PayloadAction<WorkstreamSprint[]>) => {
      state.workstreamSprints = action.payload;
    },
  },
});

export const {
  setStartDate, setSprintLength, setTotalSprints, setSprintOverride, setSprintSettings,
  clearSprintOverrides, removeMemberFromOverrides,
  addWorkstreamSprint, updateWorkstreamSprint, removeWorkstreamSprint,
  reorderWorkstreamSprints, setWorkstreamSprints,
} = sprintsSlice.actions;
export default sprintsSlice.reducer;

// ── Selectors ──────────────────────────────────────────────────────────────

/** Returns the official sprints for a workstream, sorted by number. */
export const selectSprintsForWorkstream = (state: RootState, workstreamId: string): WorkstreamSprint[] =>
  state.sprints.workstreamSprints
    .filter((s) => s.workstreamId === workstreamId)
    .sort((a, b) => a.number - b.number);

/** Returns the official sprint matching a given sprint number for a workstream. */
export const selectSprintByNumber = (
  state: RootState,
  workstreamId: string,
  number: number,
): WorkstreamSprint | undefined =>
  state.sprints.workstreamSprints.find(
    (s) => s.workstreamId === workstreamId && s.number === number,
  );
